import React, { createContext, useContext, useState, useCallback } from 'react';
import { AlertCircle, CheckCircle, Info, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info';

export interface ToastMessage {
  id: string;
  type: ToastType;
  message: string;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const showToast = useCallback((message: string, type: ToastType = 'info') => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev, { id, type, message }]);
    
    // Automatically dismiss toast after 4 seconds
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const dismissToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      
      {/* Toast container overlay */}
      <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 max-w-sm w-full">
        {toasts.map((t) => {
          const styles = {
            success: { bar: 'bg-emerald-500', icon: 'text-emerald-400' },
            error:   { bar: 'bg-rose-500',    icon: 'text-rose-400'    },
            info:    { bar: 'bg-blue-500',     icon: 'text-blue-400'   },
          }[t.type];

          return (
          <div
            key={t.id}
            className="flex items-center gap-3 pl-0 pr-4 py-3 rounded-xl bg-slate-900 border border-white/10 shadow-2xl overflow-hidden"
            style={{ animation: 'slideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards' }}
          >
            {/* left colour bar */}
            <div className={`w-1 self-stretch rounded-r shrink-0 ${styles.bar}`} />

            {t.type === 'success' && <CheckCircle className={`${styles.icon} shrink-0`} size={18} />}
            {t.type === 'error'   && <AlertCircle className={`${styles.icon} shrink-0`} size={18} />}
            {t.type === 'info'    && <Info         className={`${styles.icon} shrink-0`} size={18} />}

            <div className="flex-1 text-sm font-medium text-white">{t.message}</div>

            <button
              onClick={() => dismissToast(t.id)}
              className="text-slate-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10 shrink-0"
            >
              <X size={15} />
            </button>
          </div>
          );
        })}
      </div>
      
      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%) translateY(10px);
            opacity: 0;
          }
          to {
            transform: translateX(0) translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
